import type { Question, RoundMeta, VisitorAnswer } from './types';

/**
 * ===========================================================================
 * ROUNDS
 * ===========================================================================
 *
 * Seventeen questions in a row is a lot to ask of someone standing in a queue
 * holding a card. So the set is served in batches: finish the first batch and
 * the result is real, the invitation is real, and carrying on is optional.
 *
 * The important consequence is in `scoreCompatibility`, which already averages
 * over *answered* questions only. Someone who stops after round one gets a
 * result computed from round one, not a result penalised for the questions
 * they chose not to answer. That was true before rounds existed; rounds are
 * what make it visible.
 *
 * The flat `Question[]` stays the single source of truth. Nothing here
 * reorders it, and a round is only ever a window onto it.
 *
 * Where a round *ends*, though, is authored — `RoundMeta.size` — rather than
 * falling out of a global batch size. The first round is the commitment
 * somebody makes when they tap the button, and it ends where it does because
 * of which questions have to be inside it, not because of how seventeen
 * happens to divide. A round with no authored size falls back to `ROUND_SIZE`,
 * so adding an eighteenth question still cannot produce an unservable set.
 */

/**
 * The fallback batch size, for rounds whose copy does not name one.
 *
 * Nothing shipped uses it — both of James's rounds declare their own size —
 * but a question added past the end of the last authored round has to land
 * somewhere, and a round of ten is a better guess than a round of one.
 */
export const ROUND_SIZE = 10;

export interface Round {
  /** 0-based position in the sequence of rounds. */
  index: number;
  meta: RoundMeta;
  questions: Question[];
  /** Index of this round's first question in the flat list. */
  startIndex: number;
  /** One past this round's last question in the flat list. */
  endIndex: number;
}

function fallbackMeta(index: number): RoundMeta {
  return {
    id: `round-${index + 1}`,
    label: `Round ${index + 1}`,
    blurb: 'A few more questions, if you would like them.',
  };
}

/**
 * Slices a question list into rounds, taking each round's length from its own
 * copy and falling back to `ROUND_SIZE` where none is given.
 *
 * A final short round is fine and expected: a round is however many questions
 * are left, never a padded batch.
 */
export function buildRounds(questions: Question[], meta: RoundMeta[]): Round[] {
  const rounds: Round[] = [];

  let start = 0;
  while (start < questions.length) {
    const index = rounds.length;
    const roundMeta = meta[index] ?? fallbackMeta(index);
    // Guarded rather than trusted: a size of 0 or a negative would loop for
    // ever, and a content typo should not hang the page.
    const size = Math.max(1, Math.floor(roundMeta.size ?? ROUND_SIZE));
    const endIndex = Math.min(start + size, questions.length);

    rounds.push({
      index,
      meta: roundMeta,
      questions: questions.slice(start, endIndex),
      startIndex: start,
      endIndex,
    });

    start = endIndex;
  }

  return rounds;
}

/** The round a given position in the flat list belongs to. */
export function roundContaining(rounds: Round[], questionIndex: number): Round | undefined {
  return rounds.find(
    (round) => questionIndex >= round.startIndex && questionIndex < round.endIndex,
  );
}

export function isRoundComplete(
  round: Round,
  answers: Record<string, VisitorAnswer>,
): boolean {
  return round.questions.every((question) => answers[question.id] !== undefined);
}

/**
 * How many rounds are finished, counted from the start and stopping at the
 * first gap.
 *
 * Deliberately not "how many rounds happen to be complete": someone who
 * skipped a question in round one and finished round two has not earned the
 * round-two framing, and telling them they had would be a lie the progress
 * bar could see through.
 */
export function completedRoundCount(
  rounds: Round[],
  answers: Record<string, VisitorAnswer>,
): number {
  let count = 0;
  for (const round of rounds) {
    if (!isRoundComplete(round, answers)) break;
    count += 1;
  }
  return count;
}

/**
 * Where to send someone who wants to keep going: the first question they have
 * not answered, or `null` when there is nothing left to ask.
 */
export function firstUnansweredIndex(
  questions: Question[],
  answers: Record<string, VisitorAnswer>,
): number | null {
  const index = questions.findIndex((question) => answers[question.id] === undefined);
  return index === -1 ? null : index;
}
